import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, shareReplay } from "rxjs";
import { environment } from "src/environments/environment";
import * as crypto from "crypto-js";
import { LocalStorageService } from "./local-storage.service";

const headers = {
  "Content-Type": "application/json",
  "auth-req": "true",
  "X-Requested-With": "XMLHttpRequest",
};

@Injectable({
  providedIn: "root",
})
export class DataService {
  /** Identical GETs started together share one response. Completed calls are not reused. */
  private readonly inflightGets = new Map<string, Observable<any>>();

  constructor(
    private readonly _http: HttpClient,
    private readonly _localStorageService: LocalStorageService
  ) {}

  post = (url: string, data: any): Observable<any> => {
    this.appendDeviceId();
    this.addHmacSignature(data);
    return this._http.post(url, data, { headers });
  };

  postPlainText = (url: string, data: any): Observable<any> => {
    this.appendDeviceId();
    this.addHmacSignature(data);
    return this._http.post(url, data, {
      responseType: 'text',
      headers: { ...headers },
    });
  };

  postwithHeaders = (url: string, data: any, extraHeaders: Record<string, string> = {}): Observable<any> => {
    this.appendDeviceId();
    this.addHmacSignature(data);
    return this._http.post(url, data, { headers: { ...headers, ...extraHeaders } });
  };


  uploadFile(url: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    const customHeaders = {
      'auth-req': 'true',
      'X-Requested-With': 'XMLHttpRequest',
      // Do NOT set 'Content-Type' here — the browser will set the correct multipart boundary automatically
    };

    this.appendDeviceId();
    // Authorization / X-Organization-Id added by JwtInterceptor for as-ai-v2 URLs.

    return this._http.post(url, formData, { headers: customHeaders });
  }

  postFormData(url: string, formData: FormData): Observable<any> {
    const customHeaders = {
      'auth-req': 'true',
      'X-Requested-With': 'XMLHttpRequest',
      // Do NOT set 'Content-Type' here — the browser will set the correct multipart boundary automatically
    };

    this.appendDeviceId();

    return this._http.post(url, formData, { 
      headers: customHeaders
      // Default responseType is 'json'
    });
  }

  /**
   * Multipart POST returning plain text (or JSON-as-string). Use when the AI OCR endpoint returns text or JSON string.
   */
  postFormDataText(url: string, formData: FormData): Observable<string> {
    const customHeaders = {
      'auth-req': 'true',
      'X-Requested-With': 'XMLHttpRequest',
    };
    this.appendDeviceId();
    return this._http.post(url, formData, {
      headers: customHeaders,
      responseType: 'text',
    });
  }

  put = (url: string, data: any): Observable<any> => {
    this.appendDeviceId();
    return this._http.put(url, data, { headers: headers });
  };

  patch = (url: string, data: any): Observable<any> => {
    this.appendDeviceId();
    return this._http.patch(url, data, { headers: headers });
  };

  get = (url: string, customOptions?: any): Observable<any> => {
    this.appendDeviceId();
    // Properly merge headers if custom headers are provided
    const mergedHeaders = customOptions?.headers 
      ? { ...headers, ...customOptions.headers }
      : headers;
    if (customOptions) {
      return this._http.get(url, {
        ...customOptions,
        headers: mergedHeaders,
      });
    }
    const existing = this.inflightGets.get(url);
    if (existing) {
      return existing;
    }
    const shared = this._http.get(url, { headers: mergedHeaders }).pipe(
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.inflightGets.set(url, shared);
    shared.subscribe({
      error: () => this.inflightGets.delete(url),
      complete: () => this.inflightGets.delete(url),
    });
    return shared;
  };

  delete = (url: string, data?: any): Observable<any> => {
    this.appendDeviceId();
    return this._http.delete(url, { headers: headers, body: data });
  };

  private addHmacSignature(data: any) {
    const payload = data ? JSON.stringify(data) : "NO_PAYLOAD";
    const signature = crypto
      .HmacSHA256(payload, environment.signatureKey)
      .toString(crypto.enc.Hex);
    headers["signature"] = signature;
  }

  private appendDeviceId() {
    if (this._localStorageService.machineId) {
      headers["user_deviceid"] = this._localStorageService.machineId;
    }
  }
}
